import React, { useState, useMemo } from "react";
import Lottie from "lottie-react";
import loader from "../assets/loader.json";

import Navbar from "../Components/Navbar";
import { DataGrid } from "@mui/x-data-grid";
import { useGetAllNftQuery } from "../redux/features/user/userApi";
// Highcharts imports
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval } from "date-fns";
import exporting from 'highcharts/modules/exporting';
import exportData from 'highcharts/modules/export-data';

if (typeof Highcharts === 'object') {
  HighchartsAccessibility(Highcharts);
  exporting(Highcharts);
  exportData(Highcharts);
}

const OrdersHistory = () => {
  const { data, isLoading } = useGetAllNftQuery();
  const [search, setSearch] = useState("");
  const [interval, setInterval] = useState("day"); // day, week, month, year
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [chartMetric, setChartMetric] = useState("quantity"); // quantity or earnedPower

  const columns = [
    { field: "id", headerName: "S.No", flex: 0.5, minWidth: 100 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 200 },
    { field: "name", headerName: "User Name", flex: 0.75, minWidth: 150 },
    { field: "quantity", headerName: "Quantity", flex: 0.5, minWidth: 100 },
    { field: "tokenId", headerName: "TokenId", flex: 0.5, minWidth: 100 },
    {
      field: "Totalearnedpowerunits",
      headerName: "Earnedpower",
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <div style={{
          backgroundColor: '#dcfce7',
          padding: '4px 8px',
          borderRadius: '4px',
          fontWeight: '500',
          color: '#166534',
          minWidth: '60px',
          textAlign: 'center'
        }}>
          {params.value || 0}
        </div>
      )
    },
    { field: "project", headerName: "Project", flex: 0.5, minWidth: 100 },
    { field: "createdAt", headerName: "Created At", flex: 0.75, minWidth: 150 },
  ];

  const rows = useMemo(() => {
    const result = [];
    if (data?.data) {
      data.data.forEach((item, index) => {
        const options = {
          year: "numeric",
          month: "long",
          day: "numeric",
        };
        const date = new Date(item.createdAt).toLocaleDateString(
          "en-US",
          options
        );
        result.push({
          id: index + 1,
          userId: item._id,
          email: item.useremail || 'N/A',
          name: item.username || 'N/A',
          tokenId: item.tokenid || 'N/A',
          Totalearnedpowerunits: item.totalearnedpowerunits || 0,
          quantity: item.Quantity || 0,
          project: item.Project || 'N/A',
          project_plan: item.Project_plan || 'N/A',
          project_price: item.Project_price || 0,
          createdAt: date,
          createdAtRaw: item.createdAt,
        });
      });
    }
    return result;
  }, [data?.data]);

  // Date filter logic
  const dateFilteredRows = useMemo(() => {
    if (!startDate && !endDate) return rows;
    return rows.filter((row) => {
      const rowDate = new Date(row.createdAtRaw);
      let afterStart = true, beforeEnd = true;
      if (startDate) afterStart = rowDate >= new Date(startDate);
      if (endDate) beforeEnd = rowDate <= new Date(endDate + 'T23:59:59');
      return afterStart && beforeEnd;
    });
  }, [rows, startDate, endDate]);

  // Filter rows based on search and date
  const filteredRows = dateFilteredRows.filter(
    (row) =>
      row.email?.toLowerCase().includes(search.toLowerCase()) ||
      row.name?.toLowerCase().includes(search.toLowerCase()) ||
      row.project?.toLowerCase().includes(search.toLowerCase())||
      row.tokenId?.toLowerCase().includes(search.toLowerCase())
  );

  // Graph-scoped month filter for chart rows
  const graphRows = useMemo(() => {
    if (!selectedMonth) return filteredRows;
    const [yearStr, monthStr] = selectedMonth.split('-');
    const year = Number(yearStr);
    const monthIndex = Number(monthStr) - 1;
    const monthStart = new Date(year, monthIndex, 1);
    const monthEnd = endOfMonth(monthStart);
    return filteredRows.filter(row => {
      const d = new Date(row.createdAtRaw);
      return d >= monthStart && d <= monthEnd;
    });
  }, [filteredRows, selectedMonth]);

  // --- Grouping logic for chart ---
  const groupedData = useMemo(() => {
    const group = {};
    graphRows.forEach((row) => {
      const dateObj = new Date(row.createdAtRaw);
      let key = "";
      if (interval === "day") {
        key = format(dateObj, "yyyy-MM-dd");
      } else if (interval === "week") {
        // ISO week number
        const weekNumber = Math.ceil(dateObj.getDate() / 7);
        key = `${dateObj.getFullYear()}-W${String(weekNumber).padStart(2, "0")}`;
      } else if (interval === "month") {
        key = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}`;
      } else if (interval === "year") {
        key = `${dateObj.getFullYear()}`;
      }
      if (!group[key]) group[key] = 0;
      // Use selected metric for chart
      const value = chartMetric === "earnedPower" ? row.Totalearnedpowerunits : row.quantity;
      group[key] += Number(value) || 0;
    });

    // Helper: get min/max date from graphRows
    const getMinMaxDate = () => {
      if (!graphRows.length) return [null, null];
      let min = new Date(graphRows[0].createdAtRaw);
      let max = new Date(graphRows[0].createdAtRaw);
      graphRows.forEach(row => {
        const d = new Date(row.createdAtRaw);
        if (d < min) min = d;
        if (d > max) max = d;
      });
      return [min, max];
    };

    // For 'day' interval, fill all days in the selected month
    if (interval === "day") {
      const [yearStr, monthStr] = (selectedMonth || format(new Date(), 'yyyy-MM')).split('-');
      const monthStart = new Date(Number(yearStr), Number(monthStr) - 1, 1);
      const monthEnd = endOfMonth(monthStart);
      const allDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
      return allDays.map(date => {
        const key = format(date, "yyyy-MM-dd");
        return {
          label: format(date, "MMM dd"),
          staked: group[key] || 0,
        };
      });
    }
    // For 'week' interval, fill all weeks in the range
    if (interval === "week" && graphRows.length > 0) {
      const [min, max] = getMinMaxDate();
      // Get all weeks between min and max
      let weeks = [];
      let current = new Date(min);
      current.setDate(1); // start at first of month
      let end = new Date(max);
      end.setDate(1);
      end.setMonth(end.getMonth() + 1);
      while (current <= end) {
        const year = current.getFullYear();
        for (let w = 1; w <= 5; w++) {
          const key = `${year}-W${String(w).padStart(2, "0")}`;
          weeks.push(key);
        }
        current.setMonth(current.getMonth() + 1);
      }
      // Remove duplicates
      weeks = [...new Set(weeks)];
      return weeks.map(k => {
        const [year, week] = k.split('-W');
        return {
          label: `W${week} ${year}`,
          staked: group[k] || 0,
        };
      });
    }
    // For 'month' interval, fill all months in the range
    if (interval === "month" && graphRows.length > 0) {
      const [min, max] = getMinMaxDate();
      let months = [];
      let current = new Date(min.getFullYear(), min.getMonth(), 1);
      const end = new Date(max.getFullYear(), max.getMonth(), 1);
      while (current <= end) {
        const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, "0")}`;
        months.push(key);
        current.setMonth(current.getMonth() + 1);
      }
      return months.map(k => {
        const [year, month] = k.split('-');
        return {
          label: format(new Date(year, Number(month) - 1), "MMM yyyy"),
          staked: group[k] || 0,
        };
      });
    }
    // For 'year' interval, fill all years in the range
    if (interval === "year" && graphRows.length > 0) {
      const [min, max] = getMinMaxDate();
      let years = [];
      for (let y = min.getFullYear(); y <= max.getFullYear(); y++) {
        years.push(String(y));
      }
      return years.map(k => ({
        label: k,
        staked: group[k] || 0,
      }));
    }
    // Sort keys
    const sortedKeys = Object.keys(group).sort((a, b) => {
      if (interval === "week") {
        const [yearA, weekA] = a.split('-W').map(Number);
        const [yearB, weekB] = b.split('-W').map(Number);
        return yearA - yearB || weekA - weekB;
      }
      return a.localeCompare(b);
    });
    return sortedKeys.map((k) => {
      let label = k;
      if (interval === "day") {
        label = format(parseISO(k), "MMM dd");
      } else if (interval === "week") {
        const [year, week] = k.split('-W');
        label = `W${week} ${year}`;
      } else if (interval === "month") {
        const [year, month] = k.split('-');
        label = format(new Date(year, Number(month) - 1), "MMM yyyy");
      } else if (interval === "year") {
        label = k;
      }
      return {
        label,
        staked: group[k],
      };
    });
  }, [graphRows, interval, chartMetric, selectedMonth]);

  // --- Highcharts options ---
  const chartOptions = useMemo(() => ({
    chart: {
      type: "areaspline",
      style: {
        fontFamily: "'Inter', 'Segoe UI', Roboto, sans-serif",
      },
      backgroundColor: "transparent",
      borderRadius: 12,
      spacing: [20, 20, 20, 20],
      animation: { duration: 1000 },
      height: 320,
    },
    title: {
      text: chartMetric === 'earnedPower' ? 'Earned Power Activity' : 'Staking Activity',
      align: 'left',
      style: {
        color: '#1E293B',
        fontSize: '18px',
        fontWeight: '600'
      },
      margin: 30
    },
    xAxis: {
      categories: groupedData.map((item) => item.label),
      title: {
        text: interval === 'day' ? 'Date' :
          interval === 'week' ? 'Week' :
            interval === 'month' ? 'Month' : 'Year',
        style: {
          fontSize: '12px',
          fontWeight: '600',
          color: '#64748B'
        }
      },
      gridLineWidth: 1,
      gridLineColor: 'rgba(100, 116, 139, 0.1)',
      lineColor: 'rgba(100, 116, 139, 0.2)',
      tickColor: 'rgba(100, 116, 139, 0.2)',
      labels: {
        style: {
          fontSize: '11px',
          color: '#64748B',
          fontWeight: '500'
        },
        rotation: groupedData.length > 10 ? -45 : 0
      },
      accessibility: {
        description: interval === 'day' ? 'Dates' :
          interval === 'week' ? 'Weeks' :
            interval === 'month' ? 'Months' : 'Years'
      },
    },
    yAxis: {
      title: {
        text: chartMetric === 'earnedPower' ? 'Total Earned Power' : 'Total Staked',
        style: {
          fontSize: '12px',
          fontWeight: '600',
          color: '#64748B'
        }
      },
      gridLineColor: 'rgba(100, 116, 139, 0.1)',
      labels: {
        style: {
          fontSize: '11px',
          color: '#64748B',
          fontWeight: '500'
        }
      },
      min: 0,
      allowDecimals: false,
      accessibility: { description: chartMetric === 'earnedPower' ? 'Number of earned power units' : 'Number of staked' },
    },
    tooltip: {
      backgroundColor: '#FFFFFF',
      borderWidth: 1,
      borderColor: '#E2E8F0',
      borderRadius: 8,
      shadow: false,
      style: {
        fontSize: '13px',
        color: '#1e293b',
        padding: '12px'
      },
      headerFormat: '<span style="font-weight: 600; font-size: 13px; color: #1E293B">{point.key}</span><br/>',
      pointFormat: `<div style="display: flex; align-items: center; gap: 8px; margin-top: 4px;"><span style="display: inline-block; width: 10px; height: 10px; background-color: {point.color}; border-radius: 2px;"></span><span style="font-weight: 500; color: #2563eb">{point.y}</span> ${chartMetric === 'earnedPower' ? 'Power Units' : 'Staked'}</div>`,
      animation: true,
      shared: true,
      useHTML: true,
    },
    plotOptions: {
      areaspline: {
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, '#3B82F6'],
            [1, 'rgba(59, 130, 246, 0.05)']
          ]
        },
        lineWidth: 3,
        color: '#3B82F6',
        marker: {
          enabled: true,
          radius: 5,
          symbol: 'circle',
          fillColor: '#fff',
          lineWidth: 2,
          lineColor: '#3B82F6',
          states: { hover: { enabled: true, radius: 7, fillColor: '#fff' } },
        },
        threshold: null,
        animation: { duration: 1200 },
        shadow: false,
        dataLabels: {
          enabled: groupedData.length <= 15,
          format: '{y}',
          style: {
            fontSize: '11px',
            color: '#1E293B',
            textOutline: 'none'
          },
          align: 'center',
          verticalAlign: 'bottom',
          y: -5
        }
      },
      series: {
        cursor: 'pointer',
        point: {
          events: {
            click: function () {
              // Handle click event if needed
            }
          }
        }
      }
    },
    series: [
      {
        name: chartMetric === 'earnedPower' ? 'Total Earned Power' : 'Total Staked',
        data: groupedData.map((item) => item.staked),
        color: '#3B82F6',
        fillOpacity: 0.3,
        accessibility: { description: chartMetric === 'earnedPower' ? 'Number of earned power units per interval' : 'Number of staked per interval' },
      },
    ],
    credits: { enabled: false },
    legend: { enabled: false },
    exporting: {
      buttons: {
        contextButton: {
          menuItems: [
            'viewFullscreen',
            'printChart',
            'separator',
            'downloadPNG',
            'downloadJPEG',
            'downloadPDF',
            'downloadSVG',
            'separator',
            'downloadCSV',
            'downloadXLS'
          ]
        }
      }
    },
    responsive: {
      rules: [
        {
          condition: { maxWidth: 600 },
          chartOptions: {
            chart: { spacing: [8, 8, 8, 8], height: 220 },
            xAxis: { labels: { rotation: -45 } },
            plotOptions: {
              column: {
                pointWidth: 8,
                dataLabels: {
                  enabled: false
                }
              }
            }
          },
        },
      ],
    },
    accessibility: {
      description: `Chart showing staking activity grouped by ${interval}`,
      screenReaderSection: {
        beforeChartFormat:
          '<h5>{chartTitle}</h5>' +
          '<div>{chartSubtitle}</div>' +
          '<div>{chartLongdesc}</div>' +
          '<div>{viewTableButton}</div>',
      },
    },
  }), [groupedData, interval, chartMetric]);

  // CSV Export function
  const exportToCSV = () => {
    const csvRows = [
      columns.map((col) => col.headerName).join(","),
      ...filteredRows.map((row) => columns.map((col) => row[col.field]).join(",")),
    ];
    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "orders_history.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const style = {
    height: 100,
    width: 100,
    margin: "auto",
    alignItem: "center",
  };

  return isLoading ? (
    <Lottie animationData={loader} style={style} loop={true} width={20} />
  ) : (
    <>
      <Navbar />
      <div className="w-[80vw] mx-auto h-full">
        <div className="w-full mt-[10px] border rounded-lg bg-white shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between p-5 text-blue-900 font-bold gap-4">
            <h1 className="text-2xl">Stake History</h1>
            {/* Controls container */}
            <div className="flex flex-col md:flex-row gap-4">
              {/* Metric selector */}
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                <span className="text-sm text-gray-600 px-2">Chart:</span>
                {[
                  { value: 'quantity', label: 'Quantity' },
                  { value: 'earnedPower', label: 'Earned Power' }
                ].map((metric) => (
                  <button
                    key={metric.value}
                    onClick={() => setChartMetric(metric.value)}
                    className={`px-3 py-1 rounded-lg font-semibold transition-colors duration-150 ${chartMetric === metric.value ? 'bg-blue-600 text-white shadow' : 'bg-transparent text-blue-700 hover:bg-blue-200'}`}
                  >
                    {metric.label}
                  </button>
                ))}
              </div>
              {/* Modern segmented control for interval selection + month picker */}
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                {['day', 'week', 'month', 'year'].map((val) => (
                  <button
                    key={val}
                    onClick={() => setInterval(val)}
                    className={`px-4 py-1 rounded-lg font-semibold transition-colors duration-150 ${interval === val ? 'bg-blue-600 text-white shadow' : 'bg-transparent text-blue-700 hover:bg-blue-200'}`}
                  >
                    {val.charAt(0).toUpperCase() + val.slice(1)}
                  </button>
                ))}
                <div className="ml-2 flex items-center gap-2">
                  <span className="text-sm text-gray-600">Month:</span>
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="border px-3 py-1 rounded"
                    aria-label="Select month for Orders chart"
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Chart Card */}
          <div className="w-full flex flex-col md:flex-row gap-4 px-5 pb-4">
            <div className="w-full md:w-3/3 bg-[#f8fafc] rounded-2xl shadow-sm border p-6 flex flex-col justify-between">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
                <div className="flex items-center gap-4">
                  <div className="text-lg font-semibold text-slate-800">
                    {chartMetric === 'earnedPower' ? 'Earned Power' : 'Staking'} Overview
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-sm bg-blue-500"></span>
                      <span>{chartMetric === 'earnedPower' ? 'Total Earned Power' : 'Total Staked'}</span>
                    </span>
                  </div>
                </div>
                <div className="flex gap-4 text-sm text-gray-600">
                  <span>Total: <span className="font-bold text-blue-700">{groupedData.reduce((acc, d) => acc + d.staked, 0)}</span></span>
                  <span>Orders: <span className="font-bold text-blue-700">{filteredRows.length}</span></span>
                  <span>Avg/Period: <span className="font-bold text-blue-700">
                    {groupedData.length > 0
                      ? Math.round(groupedData.reduce((acc, d) => acc + d.staked, 0) / groupedData.length)
                      : 0}
                  </span></span>
                </div>
              </div>
              <HighchartsReact
                highcharts={Highcharts}
                options={chartOptions}
                containerProps={{ style: { height: "320px", width: "100%" } }}
              />
            </div>
          </div>
          {/* Search, Date Filter, and Export controls inside order history div */}
          <div className="flex flex-col md:flex-row justify-between items-center px-5 pb-4 gap-2 md:gap-4">
            <input
              type="text"
              placeholder="Search by email, name, or project..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border px-3 py-2 rounded-lg w-full md:w-1/3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            />
            <div className="flex items-center gap-2 w-full md:w-auto justify-start md:justify-center">
              <span className="font-semibold text-gray-700 whitespace-nowrap">Date Range:</span>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="border px-3 py-1 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                max={endDate || undefined}
              />
              <span className="mx-1 text-gray-500">to</span>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="border px-3 py-1 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                min={startDate || undefined}
              />
            </div>
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2 w-full md:w-auto justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Export CSV
            </button>
          </div>
          <div className="px-5 pb-5">
            <DataGrid
              columns={columns}
              rows={filteredRows}
              pageSize={25}
              rowsPerPageOptions={[25]}
              autoHeight
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f1f5f9',
                  borderRadius: '8px 8px 0 0',
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e2e8f0',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f8fafc',
                },
                '& .MuiDataGrid-columnHeader[data-field="Totalearnedpowerunits"]': {
                  backgroundColor: '#dcfce7',
                  color: '#166534',
                  fontWeight: '600',
                },
                '& .MuiDataGrid-cell[data-field="Totalearnedpowerunits"]': {
                  backgroundColor: 'transparent',
                }
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default OrdersHistory;